import { lazy, Suspense, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';

import FEATURES from '../../constants/featureToggling';
import { WIDGET_NAMES } from '../../constants/widgetNames';
import {
  ANALYTICS_SCREEN_AREAS,
  ANALYTICS_SCREEN_NAMES,
} from '../../constants/analytics';
import * as ROUTE_TEMPLATES from '../../constants/routeTemplates';
import { useWidgets } from '../../hooks/useWidgets';
import SliderNavigationWidgetSkeleton from '../../components/SliderNavigation/components/Skeleton';
import { StickyContainer } from './StickyContainer';
import type { IWidget } from '../../hooks/useWidgets/types';
import type { TSliderNavigationWidget } from './types';

import styles from './index.module.css';

const WIDGETS_MAP = {
  [WIDGET_NAMES.SLIDER_NAVIGATION]: {
    Widget: lazy(
      () => import('../../components/widgets/SliderNavigationWidget')
    ),
    Skeleton: SliderNavigationWidgetSkeleton,
    predicate({ growthbook }) {
      return growthbook.isOn(
        FEATURES.ENABLED_SITEWIDE_SLIDER_NAVIGATION_WIDGET
      );
    },
  },
};

const analyticsData = {
  screen_name: ANALYTICS_SCREEN_NAMES.SITEWIDE_NAVBAR,
  screen_area: ANALYTICS_SCREEN_AREAS.WIDGET,
};

export const SitewideSliderNavigationWidget = () => {
  const { path } = useRouteMatch();
  const widgets = useWidgets(WIDGETS_MAP);

  const widgetsList = useMemo(
    () =>
      widgets.slice(0, 1) as Array<IWidget<TSliderNavigationWidget['config']>>,
    [widgets]
  );

  switch (path) {
    case ROUTE_TEMPLATES.BONUSES_LIST:
    case ROUTE_TEMPLATES.BONUSES_HISTORY:
    case ROUTE_TEMPLATES.BONUS_DETAIL:
    case ROUTE_TEMPLATES.CAMPAIGN_DETAIL:
    case ROUTE_TEMPLATES.HALL_OF_FAME:
    case ROUTE_TEMPLATES.HALL_OF_FAME_HISTORY:
    case ROUTE_TEMPLATES.BONUS_GAMES_WAGERING:
    case ROUTE_TEMPLATES.BONUS_GAMES_COLLECTING:
    case ROUTE_TEMPLATES.CATEGORY_LIST:
    case ROUTE_TEMPLATES.CATEGORY_DETAIL:
    case ROUTE_TEMPLATES.CATEGORY_DETAIL_PAGINATED:
    case ROUTE_TEMPLATES.PROVIDER_LIST:
    case ROUTE_TEMPLATES.PROVIDER_DETAIL:
    case ROUTE_TEMPLATES.PROVIDER_DETAIL_PAGINATED:
    case ROUTE_TEMPLATES.THEME_LIST:
    case ROUTE_TEMPLATES.THEME_DETAIL:
    case ROUTE_TEMPLATES.THEME_DETAIL_PAGINATED:
    case ROUTE_TEMPLATES.FAVORITES:
    case ROUTE_TEMPLATES.RECENT_GAMES:
    case ROUTE_TEMPLATES.ROOT: {
      return (
        <>
          {widgetsList.map((widget, index) => {
            const config = WIDGETS_MAP[widget.name];
            const { Widget, Skeleton } = config;

            const widgetID = widget.name + index;

            return Widget ? (
              <StickyContainer config={widget.config}>
                <Suspense
                  key={widgetID}
                  fallback={
                    <Skeleton
                      className={styles.slider}
                      config={widget.config}
                    />
                  }
                >
                  <Widget
                    config={widget.config}
                    analyticsData={analyticsData}
                    className={styles.slider}
                  />
                </Suspense>
              </StickyContainer>
            ) : null;
          })}
        </>
      );
    }

    default:
      return null;
  }
};
